/**
 * EXPORT mode — the one and only place a character leaves Motion Kit.
 *
 * The default product is a LIGHT, rig-only GLB: animations travel as shared JSON clips played by
 * the runtime SDK, so nothing here requires a clip. The left column is the opt-in list of clips to
 * bake INTO the GLB for a consumer that runs no SDK; the right column is the proof of what the last
 * export produced. The export ITSELF (file name + button) lives in the footer strip
 * (`exportFooterPanel.ts`), where it cannot scroll away from the user.
 *
 * The embed list draws the same row as the LIBRARY list (`clipRow`) — one row shape, two tick
 * sources: here the persisted `library.selectedClipIds`, there the ephemeral `librarySelection`.
 */

import { DEFAULT_FOLDER } from '../../shared';
import type { EditorContext } from '../context';
import type { Controllers } from '../controllers/types';
import type { LibraryClipSummary } from '../types';
import { el, icon } from './dom';
import { clipRow } from './libraryPanel';
import { createPanel, note, runAsync, section, type PanelHandle } from './panel';

/**
 * Folder display order: General first (it is the implicit home of every unassigned clip), then the
 * user's folders in their own order, then any folder a summary claims that the folder list does
 * not know about — defensive, so a clip can never become invisible because of a stale list.
 */
function folderOrder(clips: readonly LibraryClipSummary[], folders: readonly string[]): string[] {
  const order: string[] = [DEFAULT_FOLDER];
  for (const name of folders) if (!order.includes(name)) order.push(name);
  for (const clip of clips) if (!order.includes(clip.folder)) order.push(clip.folder);
  return order;
}

/** LEFT column: which library clips get baked into the GLB (optional, off by default). */
export function mountExportEmbedPanel(
  container: HTMLElement,
  ctx: EditorContext,
  controllers: Controllers,
): PanelHandle {
  const { store } = ctx;

  function render(root: HTMLElement): void {
    const state = store.getState();
    const clips = state.library.clips;
    const selected = new Set(state.library.selectedClipIds);
    const ticked = clips.filter((clip) => selected.has(clip.id)).length;

    const rows: HTMLElement[] = [
      note(
        'Optional: ticked clips are baked into the GLB for players that run no SDK. Leave them ' +
          'all unticked to export a light rig-only character and ship the animations as clip ' +
          'files.',
      ),
    ];

    if (clips.length === 0) {
      rows.push(note('No clips in the library yet.'));
    } else {
      for (const folder of folderOrder(clips, state.library.folders)) {
        const inFolder = clips.filter((clip) => clip.folder === folder);
        if (inFolder.length === 0) continue;
        // No folder tick box here: embedding is per clip, and a folder-wide tick would write the
        // persisted export set of a whole group in one unremarkable click.
        rows.push(
          el('div', { class: 'row folder', 'data-folder': folder }, [
            icon('folder', 12),
            el('span', { class: 'grow', text: folder }),
          ]),
        );
        for (const clip of inFolder) {
          rows.push(
            clipRow(clip, {
              role: 'embed',
              checked: selected.has(clip.id),
              meta: `${clip.durationFrames}f · ${clip.fps}fps`,
              onToggle: () => runAsync(controllers.library.toggleSelected(clip.id)),
            }),
          );
        }
      }
    }

    rows.push(note(`${ticked} of ${clips.length} clips will be embedded`, 'note status'));

    root.appendChild(
      section('Embed animations', rows, {
        icon: 'film',
        meta: `${ticked}/${clips.length}`,
        key: 'export-embed',
      }),
    );
  }

  return createPanel(container, ctx, render, {
    className: 'export-embed',
    slices: [(s) => s.library, (s) => s.project],
    events: ['rig-changed', 'model-changed'],
  });
}

/** RIGHT column: what the last export produced. The export itself is in the footer. */
export function mountExportPanel(
  container: HTMLElement,
  ctx: EditorContext,
  _controllers: Controllers,
): PanelHandle {
  const { store } = ctx;

  function render(root: HTMLElement): void {
    const last = store.getState().ui.lastExport;

    // Nothing to prove yet — but the column still says what this mode is about.
    if (!last) {
      root.appendChild(
        section('Export', [note('No export yet.')], { icon: 'download', key: 'export-main' }),
      );
      return;
    }

    const children: HTMLElement[] = [
      note(`${last.boneNames.length} bones · ${last.clips.length} clips`),
      /*
       * Three states, not two. `hasPositionOrScaleTracks` is the invariant alarm — a position or
       * scale track the format does not allow — and it stays the loud line. The Hips vertical
       * offset IS allowed (a crouch has to reach the game), so a GLB carrying it says so plainly
       * instead of reading "none", which would be a lie the moment the channel is used.
       */
      note(
        `position tracks: ${
          last.hasPositionOrScaleTracks
            ? 'YES — invariant broken'
            : last.hipsPositionTrackCount > 0
              ? 'Hips only (vertical offset)'
              : 'none'
        }`,
        last.hasPositionOrScaleTracks ? 'note bad' : 'note ok',
      ),
    ];
    if (last.clips.length === 0) {
      children.push(note('rig-only — no animations embedded'));
    } else {
      const list = el('ul', { class: 'clipnames' });
      for (const clip of last.clips) {
        list.appendChild(el('li', { text: `${clip.name} (${clip.duration.toFixed(2)}s)` }));
      }
      children.push(list);
    }

    /*
     * The texture line, only for an export that actually left textures out. A normal export says
     * nothing here rather than "textures: embedded" — the file carrying its own images is the
     * default, and a line stating the default on every export is noise.
     *
     * The zero-file case is called out separately because it is the one that looks like a failure
     * and is not: a model with no textures produces no image files, and the user should read that
     * as "nothing to extract", not "the extraction silently did nothing".
     */
    if (last.textures) {
      const { fileCount, totalBytes, manifestFile, materialCount, warnings } = last.textures;
      children.push(
        fileCount === 0
          ? note('textures: none in this model — nothing was extracted')
          : note(
              `textures: ${fileCount} file(s), ${Math.round(totalBytes / 1024)} KB kept out of ` +
                `the GLB · ${materialCount} material(s) in ${manifestFile}`,
              'note ok',
            ),
      );
      for (const warning of warnings) children.push(note(warning, 'note bad'));
    }

    root.appendChild(section('Last export', children, { icon: 'check', key: 'export-last' }));
  }

  return createPanel(container, ctx, render, {
    className: 'export',
    slices: [(s) => s.ui.lastExport],
  });
}
